import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { API } from '../../context/AuthContext';
import {
  Building2,
  Phone,
  MapPin,
  FileText,
  Target,
  Users,
  Share2,
  Settings,
  Check,
  Plus,
  Save,
  Send,
  Globe,
  Mail,
  MapPinned,
  UserCircle,
  Briefcase,
  Camera,
  Image,
  Upload,
  Shield,
  Star,
  Calendar,
  DollarSign,
  Clock,
  Trash2,
} from 'lucide-react';

const EMPTY = {
  org_name:'', acronym:'', tagline:'', reg_status:'', reg_number:'', legal_status:'', registration_date:'', org_type:'',
  phone:'', phone2:'', email:'', email2:'', website:'', facebook:'', instagram:'', youtube:'', x_twitter:'', linkedin:'', whatsapp:'',
  facebook_followers:'', x_followers:'', linkedin_followers:'', youtube_subscribers:'',
  physical_address:'', province_id:'', district_id:'', ward:'', gps_lat:'', gps_lng:'',
  contact_name:'', contact_designation:'', contact_phone:'', contact_email:'',
  mission:'', vision:'', aim:'', background:'', objectives:'', year_established:'',
  staff_strength:'', annual_budget:'', main_donors:'', org_role:'', current_funding:'', grants:'',
  member_zan:false, member_nac:false, member_nango:false, member_mopslsw:false, member_other:'',
  women_led:false, youth_led:false,
  cluster_ovc:false, cluster_kp:false, cluster_health:false, cluster_gender:false, cluster_advocacy:false, cluster_other:'',
  operational_status:'active', is_published:false, is_featured:false,
  thematic_area_ids:[], focus_area_ids:[],
  directors:[],
  programs: [],   // NEW: programs array
};

const SECTIONS = [
  { key:'identity',    label:'Identity',        icon: Building2 },
  { key:'contact',     label:'Contact & Location', icon: Phone },
  { key:'about',       label:'About',           icon: FileText },
  { key:'programs',    label:'Programs & Projects', icon: Target },
  { key:'focus',       label:'Focus & Members', icon: Target },
  { key:'people',      label:'Directors',       icon: Users },
  { key:'social',      label:'Social Media',    icon: Share2 },
  { key:'settings',    label:'Settings',        icon: Settings },
];

const F = ({ label, children, required }) => (
  <div className="form-group">
    <label className="form-label">{label}{required && <span className="req"> *</span>}</label>
    {children}
  </div>
);

export default function OrgForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState(EMPTY);
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [thematics, setThematics] = useState([]);
  const [focusAreas, setFocusAreas] = useState([]);
  const [activeSection, setActiveSection] = useState('identity');
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [savedId, setSavedId] = useState(id || null);

  useEffect(() => {
    Promise.all([
      API.get('/lookup/provinces'),
      API.get('/lookup/thematic'),
      API.get('/lookup/focus'),
    ]).then(([p, t, f]) => {
      setProvinces(p.data);
      setThematics(t.data);
      setFocusAreas(f.data);
    });
  }, []);

  useEffect(() => {
    if (form.province_id) {
      API.get(`/lookup/districts?province_id=${form.province_id}`)
        .then(r => setDistricts(r.data));
    } else {
      setDistricts([]);
    }
  }, [form.province_id]);

  useEffect(() => {
    if (!isEdit) return;
    API.get(`/csos/id/${id}`)
      .then(r => {
        const d = r.data;
        setForm({
          ...EMPTY, ...d,
          province_id: d.province_id || '',
          district_id: d.district_id || '',
          year_established: d.year_established || '',
          registration_date: d.registration_date ? d.registration_date.slice(0,10) : '',
          thematic_area_ids: d.thematic_area_ids || [],
          focus_area_ids: d.focus_area_ids || [],
          directors: d.directors?.map(x => ({ full_name:x.full_name||'', id_number:x.id_number||'', email:x.email||'', phone:x.phone||'', role:x.role||'Director' })) || [],
          programs: d.programs?.map(p => ({
            id: p.id, // preserve existing IDs for updates
            program_name: p.program_name || '',
            description: p.description || '',
            start_date: p.start_date ? p.start_date.slice(0,10) : '',
            end_date: p.end_date ? p.end_date.slice(0,10) : '',
            funders: p.funders || '',
            budget: p.budget || '',
            beneficiaries: p.beneficiaries || '',
            status: p.status || 'active',
          })) || [],
          facebook_followers: d.facebook_followers || '',
          x_followers: d.x_followers || '',
          linkedin_followers: d.linkedin_followers || '',
          youtube_subscribers: d.youtube_subscribers || '',
          whatsapp: d.whatsapp || '',
        });
        setSavedId(d.id);
      })
      .catch(() => toast.error('Failed to load CSO'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleTA = id => set('thematic_area_ids', form.thematic_area_ids.includes(id) ? form.thematic_area_ids.filter(x=>x!==id) : [...form.thematic_area_ids, id]);
  const toggleFA = id => set('focus_area_ids', form.focus_area_ids.includes(id) ? form.focus_area_ids.filter(x=>x!==id) : [...form.focus_area_ids, id]);

  // Director helpers
  const addDirector = () => set('directors', [...form.directors, { full_name:'', id_number:'', email:'', phone:'', role:'Director' }]);
  const updateDir = (i, k, v) => set('directors', form.directors.map((d,idx) => idx===i ? {...d,[k]:v} : d));
  const removeDir = i => set('directors', form.directors.filter((_,idx)=>idx!==i));

  // Program helpers
  const addProgram = () => set('programs', [...form.programs, {
    program_name: '',
    description: '',
    start_date: '',
    end_date: '',
    funders: '',
    budget: '',
    beneficiaries: '',
    status: 'active',
  }]);
  const updateProg = (i, k, v) => set('programs', form.programs.map((p,idx) => idx===i ? {...p,[k]:v} : p));
  const removeProg = i => set('programs', form.programs.filter((_,idx)=>idx!==i));

  const handleSave = async (publish) => {
    if (!form.org_name.trim()) { toast.error('Organisation name is required'); setActiveSection('identity'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        province_id: form.province_id || null,
        district_id: form.district_id || null,
        year_established: form.year_established || null,
        gps_lat: form.gps_lat || null,
        gps_lng: form.gps_lng || null,
        registration_date: form.registration_date || null,
        facebook_followers: form.facebook_followers || null,
        x_followers: form.x_followers || null,
        linkedin_followers: form.linkedin_followers || null,
        youtube_subscribers: form.youtube_subscribers || null,
        whatsapp: form.whatsapp || null,
        is_published: publish !== undefined ? publish : form.is_published,
      };

      let csoId = savedId;
      if (isEdit) {
        await API.put(`/csos/${id}`, payload);
      } else {
        const res = await API.post('/csos', payload);
        csoId = res.data.id;
        setSavedId(csoId);
      }

      // Upload files
      const uploadFile = async (file, type, title) => {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('media_type', type);
        fd.append('title', title);
        const r = await API.post(`/media/${csoId}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (type === 'logo' || title === 'Logo') {
          await API.put(`/csos/${csoId}`, { ...payload, logo_url: r.data.file_url, is_published: payload.is_published });
        }
        if (type === 'banner' || title === 'Banner') {
          await API.put(`/csos/${csoId}`, { ...payload, banner_url: r.data.file_url, is_published: payload.is_published });
        }
      };

      if (logoFile) await uploadFile(logoFile, 'photo', 'Logo');
      if (bannerFile) await uploadFile(bannerFile, 'photo', 'Banner');
      for (const f of galleryFiles) {
        const fd = new FormData();
        fd.append('file', f);
        fd.append('media_type', 'photo');
        await API.post(`/media/${csoId}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }

      toast.success(isEdit ? 'Organisation updated' : 'Organisation created');
      navigate(isEdit ? `/admin/organisations/${csoId}` : '/admin/organisations');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="loading-center"><div className="spin"/></div>;

  const CheckPill = ({ checked, label, onChange }) => (
    <label style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'7px 14px', border:`1.5px solid ${checked?'var(--green)':'var(--gray-200)'}`, borderRadius:20, cursor:'pointer', background:checked?'var(--green-50)':'white', color:checked?'var(--green)':'var(--gray-600)', fontSize:'0.82rem', fontWeight:checked?600:400, transition:'all 0.2s', userSelect:'none' }}>
      <input type="checkbox" checked={checked} onChange={onChange} style={{ display:'none' }}/>
      {checked && <Check size={14} style={{ marginRight: 2 }} />}{label}
    </label>
  );

  return (
    <div>
      {/* Section tabs */}
      <div className="tab-list" style={{ marginBottom:20 }}>
        {SECTIONS.map(s => {
          const SectionIcon = s.icon;
          return (
            <button 
              key={s.key} 
              className={`tab-btn${activeSection===s.key?' active':''}`} 
              onClick={() => setActiveSection(s.key)}
            >
              <SectionIcon size={16} style={{ marginRight: 6 }} />
              {s.label}
            </button>
          );
        })}
      </div>

      <div className="card card-pad">

        {/* ── IDENTITY ── */}
        {activeSection==='identity' && (
          <div>
            <div className="grid-2">
              <F label="Organisation Name" required>
                <input className="form-control" value={form.org_name} onChange={e=>set('org_name',e.target.value)} placeholder="Full registered name"/>
              </F>
              <F label="Acronym / Short Name">
                <input className="form-control" value={form.acronym} onChange={e=>set('acronym',e.target.value)} placeholder="e.g. ZAN"/>
              </F>
              <F label="Tagline">
                <input className="form-control" value={form.tagline} onChange={e=>set('tagline',e.target.value)} placeholder="Short descriptive tagline"/>
              </F>
              <F label="Organisation Type">
                <select className="form-control" value={form.org_type} onChange={e=>set('org_type',e.target.value)}>
                  <option value="">Select type...</option>
                  {['NGO','CBO','Trust','PVO','Network / Membership Organisation','FBO','Government-Affiliated','International NGO', 'Business'].map(t=><option key={t}>{t}</option>)}
                </select>
              </F>
              <F label="Registration Status">
                <select className="form-control" value={form.reg_status} onChange={e=>set('reg_status',e.target.value)}>
                  <option value="">Select...</option>
                  {['Registered','Pending','Deregistered','Trust','Exempted'].map(s=><option key={s}>{s}</option>)}
                </select>
              </F>
              <F label="Registration Number">
                <input className="form-control" value={form.reg_number} onChange={e=>set('reg_number',e.target.value)} placeholder="e.g. PVO 17/92"/>
              </F>
              <F label="Legal Status">
                <input className="form-control" value={form.legal_status} onChange={e=>set('legal_status',e.target.value)} placeholder="e.g. Private Voluntary Organisation"/>
              </F>
              <F label="Registration Date">
                <input type="date" className="form-control" value={form.registration_date} onChange={e=>set('registration_date',e.target.value)}/>
              </F>
              <F label="Year Established">
                <input type="number" className="form-control" value={form.year_established} onChange={e=>set('year_established',e.target.value)} placeholder="e.g. 1992"/>
              </F>
              <F label="Operational Status">
                <select className="form-control" value={form.operational_status} onChange={e=>set('operational_status',e.target.value)}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                  <option value="winding_up">Winding Up</option>
                </select>
              </F>
              <F label="Staff Strength">
                <input className="form-control" value={form.staff_strength} onChange={e=>set('staff_strength',e.target.value)} placeholder="e.g. 25-50"/>
              </F>
              <F label="Annual Budget">
                <input className="form-control" value={form.annual_budget} onChange={e=>set('annual_budget',e.target.value)} placeholder="e.g. $250,000 - $500,000"/>
              </F>
            </div>
            <F label="Main Donors / Funders">
              <input className="form-control" value={form.main_donors} onChange={e=>set('main_donors',e.target.value)} placeholder="e.g. Global Fund, PEPFAR, UNAIDS"/>
            </F>
            <div className="grid-2">
              <F label="Organisation Logo">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Camera size={18} />
                  <input type="file" accept="image/*" className="form-control" onChange={e=>setLogoFile(e.target.files[0])}/>
                </div>
                {form.logo_url && <p style={{ fontSize:'0.75rem', color:'var(--green)', marginTop:4 }}><Check size={14} style={{ marginRight: 4 }} />Logo already set</p>}
              </F>
              <F label="Banner / Cover Image">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Image size={18} />
                  <input type="file" accept="image/*" className="form-control" onChange={e=>setBannerFile(e.target.files[0])}/>
                </div>
                {form.banner_url && <p style={{ fontSize:'0.75rem', color:'var(--green)', marginTop:4 }}><Check size={14} style={{ marginRight: 4 }} />Banner already set</p>}
              </F>
            </div>
          </div>
        )}

        {/* ── CONTACT & LOCATION ── */}
        {activeSection==='contact' && (
          <div>
            <h4 style={{ color:'var(--green)', marginBottom:14, fontWeight:700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Phone size={18} /> Organisation Contact
            </h4>
            <div className="grid-2">
              <F label="Primary Phone"><input className="form-control" value={form.phone} onChange={e=>set('phone',e.target.value)} placeholder="+263 ..."/></F>
              <F label="Secondary Phone"><input className="form-control" value={form.phone2} onChange={e=>set('phone2',e.target.value)}/></F>
              <F label="Primary Email"><input type="email" className="form-control" value={form.email} onChange={e=>set('email',e.target.value)}/></F>
              <F label="Secondary Email"><input type="email" className="form-control" value={form.email2} onChange={e=>set('email2',e.target.value)}/></F>
            </div>
            <h4 style={{ color:'var(--green)', margin:'20px 0 14px', fontWeight:700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <MapPinned size={18} /> Physical Location
            </h4>
            <div className="grid-2">
              <F label="Physical Address"><input className="form-control" value={form.physical_address} onChange={e=>set('physical_address',e.target.value)}/></F>
              <F label="Province">
                <select className="form-control" value={form.province_id} onChange={e=>set('province_id',e.target.value)}>
                  <option value="">Select province...</option>
                  {provinces.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </F>
              <F label="District">
                <select className="form-control" value={form.district_id} onChange={e=>set('district_id',e.target.value)}>
                  <option value="">Select district...</option>
                  {districts.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </F>
              <F label="Ward"><input className="form-control" value={form.ward} onChange={e=>set('ward',e.target.value)}/></F>
              <F label="GPS Latitude"><input className="form-control" value={form.gps_lat} onChange={e=>set('gps_lat',e.target.value)} placeholder="-17.8292"/></F>
              <F label="GPS Longitude"><input className="form-control" value={form.gps_lng} onChange={e=>set('gps_lng',e.target.value)} placeholder="31.0522"/></F>
            </div>
            <h4 style={{ color:'var(--green)', margin:'20px 0 14px', fontWeight:700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <UserCircle size={18} /> Contact Person
            </h4>
            <div className="grid-2">
              <F label="Contact Name"><input className="form-control" value={form.contact_name} onChange={e=>set('contact_name',e.target.value)}/></F>
              <F label="Designation"><input className="form-control" value={form.contact_designation} onChange={e=>set('contact_designation',e.target.value)}/></F>
              <F label="Contact Phone"><input className="form-control" value={form.contact_phone} onChange={e=>set('contact_phone',e.target.value)}/></F>
              <F label="Contact Email"><input type="email" className="form-control" value={form.contact_email} onChange={e=>set('contact_email',e.target.value)}/></F>
            </div>
          </div>
        )}

        {/* ── ABOUT ── */}
        {activeSection==='about' && (
          <div>
            <F label="Mission"><textarea className="form-control" style={{ minHeight:80 }} value={form.mission} onChange={e=>set('mission',e.target.value)}/></F>
            <F label="Vision"><textarea className="form-control" style={{ minHeight:80 }} value={form.vision} onChange={e=>set('vision',e.target.value)}/></F>
            <F label="Aim / Core Business"><textarea className="form-control" value={form.aim} onChange={e=>set('aim',e.target.value)}/></F>
            <F label="Objectives (one per line — will display as bullet points)">
              <textarea className="form-control" style={{ minHeight:100 }} value={form.objectives} onChange={e=>set('objectives',e.target.value)} placeholder="Promote HIV prevention&#10;Support PLHIV&#10;Advocate for inclusive health policies"/>
            </F>
            <F label="Background / History"><textarea className="form-control" style={{ minHeight:100 }} value={form.background} onChange={e=>set('background',e.target.value)}/></F>
            <div className="grid-2">
              <F label="Organisation Role / Specific Interventions"><textarea className="form-control" value={form.org_role} onChange={e=>set('org_role',e.target.value)}/></F>
              <div>
                <F label="Current Funding"><input className="form-control" value={form.current_funding} onChange={e=>set('current_funding',e.target.value)}/></F>
                <F label="Grants"><input className="form-control" value={form.grants} onChange={e=>set('grants',e.target.value)}/></F>
              </div>
            </div>
          </div>
        )}

        {/* ── PROGRAMS & PROJECTS ── */}
        {activeSection==='programs' && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:16 }}>
              <h4 style={{ fontWeight:700, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Target size={18} /> Programs & Projects
              </h4>
              <button type="button" onClick={addProgram} className="btn btn-sm btn-secondary">
                <Plus size={16} /> Add Program
              </button>
            </div>
            {form.programs.length===0 && (
              <div className="empty-state">
                <div className="empty-state-icon">
                  <Target size={48} />
                </div>
                <p>No programs added yet. Click "Add Program" to begin.</p>
              </div>
            )}
            {form.programs.map((p,i)=>(
              <div key={i} className="card" style={{ padding:18, marginBottom:14, background:'var(--gray-50)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:12 }}>
                  <span style={{ fontWeight:600, fontSize:'0.85rem' }}>
                    <Target size={16} style={{ marginRight: 6 }} />
                    Program {i+1}
                  </span>
                  <button type="button" onClick={()=>removeProg(i)} className="btn btn-sm btn-danger">
                    <Trash2 size={14} /> Remove
                  </button>
                </div>
                <div className="grid-2">
                  <F label="Program Name"><input className="form-control" value={p.program_name} onChange={e=>updateProg(i,'program_name',e.target.value)} placeholder="e.g. HIV Prevention Outreach"/></F>
                  <F label="Status">
                    <select className="form-control" value={p.status} onChange={e=>updateProg(i,'status',e.target.value)}>
                      <option value="active">Active</option>
                      <option value="completed">Completed</option>
                      <option value="planned">Planned</option>
                    </select>
                  </F>
                  <F label="Start Date"><input type="date" className="form-control" value={p.start_date} onChange={e=>updateProg(i,'start_date',e.target.value)}/></F>
                  <F label="End Date"><input type="date" className="form-control" value={p.end_date} onChange={e=>updateProg(i,'end_date',e.target.value)}/></F>
                  <F label="Funders / Donors"><input className="form-control" value={p.funders} onChange={e=>updateProg(i,'funders',e.target.value)} placeholder="e.g. Global Fund"/></F>
                  <F label="Budget"><input className="form-control" value={p.budget} onChange={e=>updateProg(i,'budget',e.target.value)} placeholder="$50,000"/></F>
                  <F label="Beneficiaries"><input className="form-control" value={p.beneficiaries} onChange={e=>updateProg(i,'beneficiaries',e.target.value)} placeholder="e.g. 500 women"/></F>
                </div>
                <F label="Description">
                  <textarea className="form-control" style={{ minHeight:80 }} value={p.description} onChange={e=>updateProg(i,'description',e.target.value)} placeholder="What is the program about?"/>
                </F>
              </div>
            ))}
          </div>
        )}

        {/* ── FOCUS & MEMBERS ── */}
        {activeSection==='focus' && (
          <div>
            <h4 style={{ color:'var(--green)', margin:'20px 0 12px', fontWeight:700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Target size={18} /> Thematic Areas
            </h4>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:20 }}>
              {thematics.map(t=>(
                <CheckPill key={t.id} checked={form.thematic_area_ids.includes(t.id)} label={t.name} onChange={()=>toggleTA(t.id)}/>
              ))}
            </div>

            <h4 style={{ color:'var(--green)', margin:'20px 0 12px', fontWeight:700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Target size={18} /> Focus Areas
            </h4>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:20 }}>
              {focusAreas.map(f=>(
                <CheckPill key={f.id} checked={form.focus_area_ids.includes(f.id)} label={f.name} onChange={()=>toggleFA(f.id)}/>
              ))}
            </div>

            <h4 style={{ color:'var(--green)', margin:'20px 0 12px', fontWeight:700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Users size={18} /> Memberships
            </h4>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:12 }}>
              {[['member_zan','ZAN'],['member_nac','NAC'],['member_nango','NANGO'],['member_mopslsw','MoPSLSW']].map(([k,l])=>(
                <CheckPill key={k} checked={form[k]} label={l} onChange={e=>set(k,e.target.checked)}/>
              ))}
            </div>
            <F label="Other Membership"><input className="form-control" style={{ maxWidth:300 }} value={form.member_other} onChange={e=>set('member_other',e.target.value)}/></F>

            <h4 style={{ color:'var(--green)', margin:'20px 0 12px', fontWeight:700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Users size={18} /> Demographics
            </h4>
            <div style={{ display:'flex', gap:10 }}>
              <CheckPill checked={form.women_led} label="Women-Led Organisation" onChange={e=>set('women_led',e.target.checked)}/>
              <CheckPill checked={form.youth_led} label="Youth-Led Organisation" onChange={e=>set('youth_led',e.target.checked)}/>
            </div>
          </div>
        )}

        {/* ── DIRECTORS ── */}
        {activeSection==='people' && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:16 }}>
              <h4 style={{ fontWeight:700, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Users size={18} /> Board Directors
              </h4>
              <button type="button" onClick={addDirector} className="btn btn-sm btn-secondary">
                <Plus size={16} /> Add Director
              </button>
            </div>
            {form.directors.length===0 && (
              <div className="empty-state">
                <div className="empty-state-icon">
                  <Users size={48} />
                </div>
                <p>No directors added yet. Click "Add Director" to begin.</p>
              </div>
            )}
            {form.directors.map((d,i)=>(
              <div key={i} className="card" style={{ padding:18, marginBottom:14, background:'var(--gray-50)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:12 }}>
                  <span style={{ fontWeight:600, fontSize:'0.85rem' }}>
                    <UserCircle size={16} style={{ marginRight: 6 }} />
                    Director {i+1}
                  </span>
                  <button type="button" onClick={()=>removeDir(i)} className="btn btn-sm btn-danger">Remove</button>
                </div>
                <div className="grid-2">
                  <F label="Full Name"><input className="form-control" value={d.full_name} onChange={e=>updateDir(i,'full_name',e.target.value)}/></F>
                  <F label="Role"><input className="form-control" value={d.role} onChange={e=>updateDir(i,'role',e.target.value)} placeholder="Director, Chairperson..."/></F>
                  <F label="ID Number"><input className="form-control" value={d.id_number} onChange={e=>updateDir(i,'id_number',e.target.value)}/></F>
                  <F label="Phone"><input className="form-control" value={d.phone} onChange={e=>updateDir(i,'phone',e.target.value)}/></F>
                  <F label="Email"><input type="email" className="form-control" value={d.email} onChange={e=>updateDir(i,'email',e.target.value)}/></F>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── SOCIAL MEDIA ── */}
        {activeSection==='social' && (
          <div>
            <h4 style={{ color:'var(--green)', marginBottom:14, fontWeight:700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Globe size={18} /> Social Media & Web Links
            </h4>
            <div className="grid-2">
              <F label="Website URL"><input className="form-control" value={form.website} onChange={e=>set('website',e.target.value)} placeholder="https://..."/></F>
              <F label="WhatsApp Channel Link"><input className="form-control" value={form.whatsapp} onChange={e=>set('whatsapp',e.target.value)} placeholder="https://wa.me/..."/></F>
              <F label="Facebook URL"><input className="form-control" value={form.facebook} onChange={e=>set('facebook',e.target.value)} placeholder="https://facebook.com/..."/></F>
              <F label="X (Twitter) URL"><input className="form-control" value={form.x_twitter} onChange={e=>set('x_twitter',e.target.value)} placeholder="https://x.com/..."/></F>
              <F label="Instagram URL"><input className="form-control" value={form.instagram} onChange={e=>set('instagram',e.target.value)}/></F>
              <F label="LinkedIn URL"><input className="form-control" value={form.linkedin} onChange={e=>set('linkedin',e.target.value)}/></F>
              <F label="YouTube URL"><input className="form-control" value={form.youtube} onChange={e=>set('youtube',e.target.value)}/></F>
            </div>
            <h4 style={{ color:'var(--green)', margin:'24px 0 14px', fontWeight:700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Upload size={18} /> Gallery Photos
            </h4>
            <F label="Upload Photos (select multiple)">
              <input type="file" multiple accept="image/*" className="form-control" onChange={e=>setGalleryFiles(Array.from(e.target.files))}/>
              {galleryFiles.length>0 && <p style={{ fontSize:'0.78rem', color:'var(--green)', marginTop:4 }}><Check size={14} style={{ marginRight: 4 }} />{galleryFiles.length} file(s) selected</p>}
            </F>
          </div>
        )}

        {/* ── SETTINGS ── */}
        {activeSection==='settings' && (
          <div>
            <h4 style={{ color:'var(--green)', marginBottom:16, fontWeight:700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Shield size={18} /> Publication Settings
            </h4>
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              {[
                { key:'is_published', label:'Published', desc:'Visible on the public CSO directory', icon: Globe },
                { key:'is_featured',  label:'Featured',  desc:'Highlighted on the homepage as a featured organisation', icon: Star },
              ].map(s => {
                const SettingIcon = s.icon;
                return (
                  <label key={s.key} style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 18px', border:`1.5px solid ${form[s.key]?'var(--green)':'var(--gray-200)'}`, borderRadius:10, cursor:'pointer', background:form[s.key]?'var(--green-50)':'white', transition:'all 0.2s' }}>
                    <div style={{ width:44, height:24, borderRadius:12, background:form[s.key]?'var(--green)':'var(--gray-300)', position:'relative', transition:'all 0.2s', flexShrink:0 }}>
                      <div style={{ width:18, height:18, borderRadius:'50%', background:'white', position:'absolute', top:3, left:form[s.key]?22:3, transition:'left 0.2s' }}/>
                    </div>
                    <input type="checkbox" checked={form[s.key]} onChange={e=>set(s.key,e.target.checked)} style={{ display:'none' }}/>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight:700, fontSize:'0.88rem', color:form[s.key]?'var(--green)':'var(--gray-700)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <SettingIcon size={16} />
                        {s.label}
                      </div>
                      <div style={{ fontSize:'0.78rem', color:'var(--gray-400)', marginLeft: 22 }}>{s.desc}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Action bar */}
      <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:20 }}>
        <button type="button" onClick={()=>navigate(-1)} className="btn btn-ghost">Cancel</button>
        <button type="button" onClick={()=>handleSave(false)} disabled={saving} className="btn btn-secondary">
          <Save size={16} style={{ marginRight: 6 }} />
          {saving ? 'Saving...' : 'Save as Draft'}
        </button>
        <button type="button" onClick={()=>handleSave(true)} disabled={saving} className="btn btn-primary">
          {saving ? (
            <><span className="spin spin-sm"/> Saving...</>
          ) : (
            <><Send size={16} style={{ marginRight: 6 }} />{isEdit ? 'Update & Publish' : 'Create & Publish'}</>
          )}
        </button>
      </div>
    </div>
  );
}